import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle } from "lucide-react";
import { fileToBase64 } from "@/utils";

interface PhotoUploadProps {
  photos: string[];
  setPhotos: (photos: string[]) => void;
  maxPhotos: number;
}

export function PhotoUpload({
  photos,
  setPhotos,
  maxPhotos,
}: PhotoUploadProps) {
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      addPhotos(Array.from(files));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    addPhotos(Array.from(files));
  };

  const addPhotos = async (files: File[]) => {
    const newPhotos: string[] = (await Promise.all(
      files.map((file) => fileToBase64(file))
    )) as string[];
    setPhotos([...photos, ...newPhotos].slice(0, maxPhotos));
  };

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: maxPhotos }).map((_, index) => (
          <div
            key={index}
            className={`relative aspect-square rounded-lg overflow-hidden ${
              dragOver ? "border-[rgb(219,39,119)]" : "border-gray-600"
            } ${photos[index] ? "" : "border-2 border-dashed"}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {photos[index] ? (
              <>
                <img
                  src={photos[index]}
                  alt={`Uploaded photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 w-6 h-6"
                  onClick={() => removePhoto(index)}
                >
                  ×
                </Button>
              </>
            ) : (
              <label
                htmlFor={`photo-upload-${index}`}
                className="flex items-center justify-center w-full h-full cursor-pointer"
              >
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id={`photo-upload-${index}`}
                />
                <PlusCircle className="w-8 h-8 text-gray-400" />
              </label>
            )}
          </div>
        ))}
      </div>
      <p className="text-gray-400 text-sm mt-2">
        Click or drag and drop to add photos. PNG, JPG, GIF up to 10MB
      </p>
    </div>
  );
}
